/**
 * Invoices: what has not gone out, what is owed, and what Xero disagrees with —
 * then raising a new one from a job.
 *
 * "Not sent" opens by default because it is the list that loses money: an
 * invoice raised and never sent is not owed, is not chased, and is not in the
 * accounts (D50). Raising is preview first, always.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { day, money, qty, titleCase } from "../lib/format";
import { Card, Chip, Empty, ErrorState, Loading, PageHeader, type Tone } from "../components/ui";
import type { ExportStatus, InvoiceStatus } from "../lib/types";

const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  draft: "neutral",
  issued: "info",
  part_paid: "warn",
  paid: "ok",
  overdue: "crit",
  written_off: "neutral",
};

type Show = "all" | "unsent" | "owing" | "overdue" | "accounts";

const SHOWS: [Show, string][] = [
  ["unsent", "Not sent"],
  ["owing", "Owing"],
  ["overdue", "Overdue"],
  ["accounts", "Xero"],
  ["all", "All"],
];

/** What the Xero column says, in words. `null` means not sent yet, which is
 *  the correct reason for an invoice not to be in Xero (D50). */
const ACCOUNTS_COPY: Record<ExportStatus, { label: string; tone: Tone }> = {
  pending: { label: "Xero: waiting", tone: "neutral" },
  retrying: { label: "Xero: retrying", tone: "warn" },
  pushed: { label: "In Xero", tone: "ok" },
  failed: { label: "Xero: needs a look", tone: "crit" },
  mismatch: { label: "Xero: totals differ", tone: "crit" },
};

export default function Invoices() {
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job") ?? "";
  const show = (params.get("show") as Show | null) ?? "unsent";

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="page">
      <PageHeader title="Invoices">
        Raised from what actually happened on the job. An invoice goes to Xero once the customer
        has it — emailed from here, or marked as handed over.
      </PageHeader>

      <div className="segs" role="tablist">
        {SHOWS.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={show === key}
            className={`seg${show === key ? " seg-on" : ""}`}
            onClick={() => setParam("show", key)}
          >
            {label}
          </button>
        ))}
      </div>

      <InvoiceList show={show} />

      <RaiseFromJob jobId={jobId} onJob={(id) => setParam("job", id || null)} />
    </div>
  );
}

function InvoiceList({ show }: { show: Show }) {
  const list = useQuery({ queryKey: ["invoices", show], queryFn: () => api.invoices.list(show) });

  if (list.isPending) return <Card><Loading what="Loading invoices" /></Card>;
  if (list.error) {
    return (
      <Card>
        <ErrorState error={list.error} onRetry={() => void list.refetch()} needs="Invoices are office work." />
      </Card>
    );
  }
  if (list.data.length === 0) {
    const empty: Record<Show, [string, string]> = {
      unsent: ["Nothing waiting to go out", "Every invoice raised has reached its customer."],
      owing: ["Nothing owing", "Every sent invoice is paid."],
      overdue: ["Nothing overdue", "No sent invoice is past its due date."],
      accounts: ["Xero is up to date", "Every sent invoice is in Xero with the same total."],
      all: ["No invoices yet", "Raise one from a job below."],
    };
    const [title, body] = empty[show];
    return (
      <Card>
        <Empty title={title}>{body}</Empty>
      </Card>
    );
  }

  return (
    <Card padded={false}>
      {list.data.map((row) => (
        <Link key={row.id} to={`/invoices/${row.id}`} className="inv-row">
          <div>
            <b className="mono">{row.number}</b> <span className="muted">{row.customer_name}</span>
            {(row.job_number || row.contract_code) && (
              <div className="tiny muted mono">{row.job_number ?? `${row.contract_code} · agreement`}</div>
            )}
          </div>
          <div className="right">
            <b className="mono">{row.kind === "credit" ? "−" : ""}{money(row.total_cents)}</b>
            {row.due_on && <div className="tiny muted">due {day(row.due_on)}</div>}
          </div>
          <div className="chips">
            {row.sent_at ? (
              <Chip tone={row.overdue ? "crit" : STATUS_TONE[row.status]}>
                {row.overdue ? "Overdue" : titleCase(row.status)}
              </Chip>
            ) : (
              <Chip tone="warn">Not sent</Chip>
            )}
            {row.kind !== "final" && <Chip tone="neutral">{row.kind === "contract" ? "Agreement" : row.kind === "credit" ? "Credit note" : titleCase(row.kind)}</Chip>}
            {row.accounts && (
              <Chip tone={ACCOUNTS_COPY[row.accounts].tone}>{ACCOUNTS_COPY[row.accounts].label}</Chip>
            )}
          </div>
        </Link>
      ))}
    </Card>
  );
}

function RaiseFromJob({ jobId, onJob }: { jobId: string; onJob: (id: string) => void }) {
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => api.jobs.list() });

  return (
    <>
      <Card title="Invoice a job">
        {jobs.isPending ? (
          <Loading what="Loading jobs" />
        ) : jobs.error ? (
          <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} needs="Invoicing is office work." />
        ) : (
          <label className="field">
            <span>Job</span>
            <select value={jobId} onChange={(e) => onJob(e.target.value)}>
              <option value="">Choose a job…</option>
              {jobs.data
                .filter((j) => j.status !== "cancelled")
                .map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.number} — {j.title}
                  </option>
                ))}
            </select>
          </label>
        )}
      </Card>
      {jobId && <Draft jobId={jobId} />}
    </>
  );
}

function Draft({ jobId }: { jobId: string }) {
  const [issued, setIssued] = useState<{ id: string; number: string } | null>(null);
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ["invoice-preview", jobId],
    queryFn: () => api.invoices.preview(jobId),
    retry: false,
  });

  const raise = useMutation({
    mutationFn: () => api.invoices.fromJob(jobId, { kind: "final", issued_from_field: false }),
    onSuccess: (invoice) => {
      setIssued({ id: invoice.id, number: invoice.number });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't raise the invoice."),
  });

  if (preview.isPending) return <Card><Loading what="Working out the bill" /></Card>;

  // A 409 here is the server refusing on business grounds — a pending
  // variation, nothing billable. That is information, not a failure.
  if (preview.error) {
    const err = preview.error;
    const businessRule = err instanceof ApiError && err.isBusinessRule;
    return (
      <Card title="Can't invoice this yet">
        <p className={`notice ${businessRule ? "notice-warn" : "notice-crit"}`}>
          {err instanceof Error ? err.message : "Something went wrong."}
        </p>
        {businessRule && (
          <p className="tiny muted" style={{ marginTop: 10 }}>
            Billing over an unapproved variation either short-changes you or bills
            for something nobody agreed. Sort the variation first.
          </p>
        )}
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Link className="btn" to={`/jobs/${jobId}`}>
            Open the job
          </Link>
        </div>
      </Card>
    );
  }

  const draft = preview.data;

  return (
    <Card
      title={`Draft for ${draft.job_number}`}
      subtitle={draft.basis === "fixed_price" ? "Fixed price" : "Time & materials"}
    >
      {issued && (
        <p className="notice notice-info" role="status">
          <b>{issued.number}</b> raised. It isn't owed — or in Xero — until the customer has it.{" "}
          <Link to={`/invoices/${issued.id}`}>Open it to send</Link>
        </p>
      )}
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      {draft.warnings.map((w) => (
        <p key={w} className="notice notice-warn">
          {w}
        </p>
      ))}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Line</th>
              <th>Qty</th>
              <th className="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {draft.lines.map((line, i) => (
              <tr key={`${line.description}-${i}`}>
                <td>{line.description}</td>
                <td className="mono">{qty(line.qty)}</td>
                <td className="right mono">{money(line.total_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="kv kv-money" style={{ marginTop: 14 }}>
        <dt>Subtotal</dt>
        <dd className="mono">{money(draft.subtotal_cents)}</dd>
        <dt>GST</dt>
        <dd className="mono">{money(draft.tax_cents)}</dd>
        <dt className="total">Total</dt>
        <dd className="mono total">{money(draft.total_cents)}</dd>
      </dl>
      {draft.deposit_applied_cents > 0 && (
        <p className="tiny muted" style={{ marginTop: 8 }}>
          The deposit ({money(draft.deposit_applied_cents)} incl. GST) is deducted as a line above. It was
          billed on its own invoice and is paid or chased there — so across the two, the job is billed once.
        </p>
      )}

      <div className="btn-row" style={{ marginTop: 14 }}>
        <button
          className="btn btn-primary"
          type="button"
          disabled={raise.isPending || Boolean(issued) || draft.lines.length === 0}
          onClick={() => raise.mutate()}
        >
          {issued ? "Raised" : raise.isPending ? "Raising…" : "Raise invoice"}
        </button>
        <Link className="btn" to={`/jobs/${jobId}`}>
          Open the job
        </Link>
        {issued && <Chip tone={STATUS_TONE.issued}>{titleCase("issued")}</Chip>}
      </div>

      <p className="tiny muted" style={{ marginTop: 10 }}>
        Raising it twice is safe — a job that already has a final invoice returns
        the one it has rather than billing again.
      </p>
    </Card>
  );
}
